import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { settingsApi, referralAdminApi, softwareAdminApi, withdrawalFeeApi } from '../services/adminApi'
import { apiClient } from '@shared/utils/api'
import PaymentChannelsManagement from './payments/PaymentChannelsManagement'

// 设置接口定义
interface SystemSettings {
  // 基础系统配置
  basic: {
    siteName: string
    siteLogo: string
    siteDescription: string
    contactEmail: string
    contactPhone: string
    contactAddress: string
    maintenanceMode: boolean
    maintenanceMessage: string
    timezone: string
    language: string
    version: string
  }

  // 用户设置
  user: {
    allowRegistration: boolean
    requireEmailVerification: boolean
    defaultAvatar: string
    defaultUserRole: string
    passwordMinLength: number
    passwordRequireUppercase: boolean
    passwordRequireNumbers: boolean
    passwordRequireSymbols: boolean
    maxLoginAttempts: number
    loginLockoutTime: number
    sessionTimeout: number
  }

  // 邮件配置
  email: {
    smtpHost: string
    smtpPort: number
    smtpUsername: string
    smtpPassword: string
    smtpEncryption: 'none' | 'ssl' | 'tls'
    fromEmail: string
    fromName: string
    enableWelcomeEmail: boolean
    enablePasswordResetEmail: boolean
    enableOrderNotificationEmail: boolean
  }

  // 支付配置
  payment: {
    enableAlipay: boolean
    alipayAppId: string
    alipayPrivateKey: string
    alipayPublicKey: string
    enableWechatPay: boolean
    wechatAppId: string
    wechatMchId: string
    wechatApiKey: string
    enablePayPal: boolean
    payPalClientId: string
    payPalClientSecret: string
    payPalMode: 'sandbox' | 'live'
    currency: string
    taxRate: number
    refundPolicy: string
  }

  // 安全和隐私
  security: {
    jwtSecret: string
    jwtExpirationTime: number
    jwtRefreshExpirationTime: number
    enableDataEncryption: boolean
    encryptionKey: string
    gdprCompliance: boolean
    privacyPolicy: string
    termsOfService: string
    cookiePolicy: string
  }

  // 备份和维护
  backup: {
    enableAutoBackup: boolean
    backupFrequency: 'daily' | 'weekly' | 'monthly'
    backupRetention: number
    backupLocation: string
    enableLogCleanup: boolean
    logRetentionDays: number
    enableTempCleanup: boolean
    tempCleanupFrequency: 'daily' | 'weekly'
    performanceMonitoring: boolean
    errorReporting: boolean
  }
}

const defaultSettings: SystemSettings = {
  basic: {
    siteName: 'TalkNect',
    siteLogo: '',
    siteDescription: '全球语言学习平台',
    contactEmail: 'support@talknect.com',
    contactPhone: '+86-400-123-4567',
    contactAddress: '北京市朝阳区xxx街道xxx号',
    maintenanceMode: false,
    maintenanceMessage: '系统维护中，请稍后访问',
    timezone: 'Asia/Shanghai',
    language: 'zh-CN',
    version: '1.0.0'
  },
  user: {
    allowRegistration: true,
    requireEmailVerification: true,
    defaultAvatar: '',
    defaultUserRole: 'student',
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireNumbers: true,
    passwordRequireSymbols: true,
    maxLoginAttempts: 5,
    loginLockoutTime: 30,
    sessionTimeout: 24
  },
  email: {
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpEncryption: 'tls',
    fromEmail: 'noreply@talknect.com',
    fromName: 'TalkNect',
    enableWelcomeEmail: true,
    enablePasswordResetEmail: true,
    enableOrderNotificationEmail: true
  },
  payment: {
    enableAlipay: false,
    alipayAppId: '',
    alipayPrivateKey: '',
    alipayPublicKey: '',
    enableWechatPay: false,
    wechatAppId: '',
    wechatMchId: '',
    wechatApiKey: '',
    enablePayPal: false,
    payPalClientId: '',
    payPalClientSecret: '',
    payPalMode: 'sandbox',
    currency: 'CNY',
    taxRate: 0.06,
    refundPolicy: '7天内无理由退款'
  },
  security: {
    jwtSecret: '',
    jwtExpirationTime: 24,
    jwtRefreshExpirationTime: 168,
    enableDataEncryption: true,
    encryptionKey: '',
    gdprCompliance: true,
    privacyPolicy: '',
    termsOfService: '',
    cookiePolicy: ''
  },
  backup: {
    enableAutoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30,
    backupLocation: '/backups',
    enableLogCleanup: true,
    logRetentionDays: 90,
    enableTempCleanup: true,
    tempCleanupFrequency: 'daily',
    performanceMonitoring: true,
    errorReporting: true
  }
}

export const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']))
  const [showPasswords, setShowPasswords] = useState<Set<string>>(new Set())
  const [hasChanges, setHasChanges] = useState(false)
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({})
  const [referrerId, setReferrerId] = useState('')
  const [freezeDays, setFreezeDays] = useState('7')
  const [softwareMeta, setSoftwareMeta] = useState({ name: '', version: '', platform: 'windows', notes: '' })
  const [softwareFile, setSoftwareFile] = useState<File | null>(null)
  
  // 提现手续费配置状态
  const [withdrawalFeeConfig, setWithdrawalFeeConfig] = useState({
    teacher_withdrawal_fee_rate: 0,
    min_withdrawal_amount: 10,
    max_withdrawal_amount: 10000
  })
  const [withdrawalFeeLoading, setWithdrawalFeeLoading] = useState(false)

  // 时区选项
  const timezoneOptions = [
    'Asia/Shanghai',
    'Asia/Tokyo',
    'Asia/Seoul',
    'Asia/Singapore',
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin'
  ]

  // 语言选项
  const languageOptions = [
    { code: 'zh-CN', name: '简体中文' },
    { code: 'zh-TW', name: '繁體中文' },
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'ja-JP', name: '日本語' },
    { code: 'ko-KR', name: '한국어' }
  ]

  // 货币选项
  const currencyOptions = [
    { code: 'CNY', name: '人民币 (¥)' },
    { code: 'USD', name: '美元 ($)' },
    { code: 'EUR', name: '欧元 (€)' },
    { code: 'GBP', name: '英镑 (£)' },
    { code: 'JPY', name: '日元 (¥)' },
    { code: 'KRW', name: '韩元 (₩)' }
  ]

  useEffect(() => {
    loadSettings()
    loadWithdrawalFeeConfig()
  }, [])
  
  // 加载提现手续费配置
  const loadWithdrawalFeeConfig = async () => {
    try {
      const response = await withdrawalFeeApi.getConfig()
      if (response.success && response.data) {
        setWithdrawalFeeConfig({
          teacher_withdrawal_fee_rate: response.data.teacher_withdrawal_fee_rate ?? 0,
          min_withdrawal_amount: response.data.min_withdrawal_amount ?? 10,
          max_withdrawal_amount: response.data.max_withdrawal_amount ?? 10000
        })
      }
    } catch (error) {
      console.error('加载提现手续费配置失败:', error)
    }
  }
  
  // 保存提现手续费配置
  const saveWithdrawalFeeConfig = async () => {
    setWithdrawalFeeLoading(true)
    try {
      const response = await withdrawalFeeApi.updateConfig(withdrawalFeeConfig)
      if (response.success) {
        toast.success('提现手续费配置已保存')
      } else {
        toast.error(response.error?.message || '保存失败')
      }
    } catch (error) {
      console.error('保存提现手续费配置失败:', error)
      toast.error('保存提现手续费配置失败')
    } finally {
      setWithdrawalFeeLoading(false)
    }
  }

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await settingsApi.getSettings()
      if (response.success && response.data) {
        setSettings({ ...defaultSettings, ...response.data })
      }
    } catch (error) {
      console.error('加载系统设置失败:', error)
      toast.error('加载系统设置失败')
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    if (!validateSettings()) {
      toast.error('请修正配置错误后再保存')
      return
    }

    try {
      setSaving(true)
      const response = await settingsApi.updateSettings(settings)
      if (response.success) {
        setLastSaveTime(new Date())
        setHasChanges(false)
        toast.success('系统设置保存成功')
      } else {
        toast.error(response.error?.message || '保存失败')
      }
    } catch (error) {
      console.error('保存系统设置失败:', error)
      toast.error('保存系统设置失败')
    } finally {
      setSaving(false)
    }
  }

  const validateSettings = (): boolean => {
    const errors: Record<string, string[]> = {}

    // 验证基础设置
    if (!settings.basic.siteName.trim()) {
      errors['basic.siteName'] = ['网站名称不能为空']
    }

    if (!settings.basic.contactEmail.trim()) {
      errors['basic.contactEmail'] = ['联系邮箱不能为空']
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.basic.contactEmail)) {
      errors['basic.contactEmail'] = ['联系邮箱格式不正确']
    }

    // 验证邮件设置
    if (settings.email.smtpPort && (settings.email.smtpPort < 1 || settings.email.smtpPort > 65535)) {
      errors['email.smtpPort'] = ['SMTP端口号必须在1-65535之间']
    }

    // 验证密码设置
    if (settings.user.passwordMinLength < 6) {
      errors['user.passwordMinLength'] = ['密码最小长度不能少于6位']
    }

    // 验证JWT设置
    if (settings.security.jwtExpirationTime < 1) {
      errors['security.jwtExpirationTime'] = ['JWT过期时间必须大于0']
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const togglePasswordVisibility = (field: string) => {
    const newShowPasswords = new Set(showPasswords)
    if (newShowPasswords.has(field)) {
      newShowPasswords.delete(field)
    } else {
      newShowPasswords.add(field)
    }
    setShowPasswords(newShowPasswords)
  }

  const updateSetting = (category: keyof SystemSettings, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }))
    setHasChanges(true)

    // 清除该字段的验证错误
    const key = `${category}.${field}`
    if (validationErrors[key]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[key]
        return newErrors
      })
    }
  }

  const resetToDefaults = () => {
    if (confirm('确定要重置所有设置为默认值吗？此操作不可撤销。')) {
      setSettings(defaultSettings)
      setHasChanges(true)
      toast.success('已重置为默认设置')
    }
  }

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)

    const exportFileDefaultName = `system-settings-${new Date().toISOString().split('T')[0]}.json`

    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()

    toast.success('设置已导出')
  }

  const importSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result as string)
        setSettings({ ...defaultSettings, ...imported })
        setHasChanges(true)
        toast.success('设置导入成功')
      } catch (error) {
        toast.error('设置文件格式错误')
      }
    }
    reader.readAsText(file)

    // 重置文件输入
    event.target.value = ''
  }

  const testEmailSettings = async () => {
    try {
      toast.loading('正在测试邮件设置...')
      // 这里应该调用测试邮件API
      await new Promise(resolve => setTimeout(resolve, 2000))
      toast.dismiss()
      toast.success('邮件测试成功')
    } catch (error) {
      toast.dismiss()
      toast.error('邮件测试失败')
    }
  }

  const testPaymentSettings = async (provider: string) => {
    try {
      toast.loading(`正在测试${provider}支付设置...`)
      await new Promise(resolve => setTimeout(resolve, 2000))
      toast.dismiss()
      toast.success(`${provider}支付测试成功`)
    } catch (error) {
      toast.dismiss()
      toast.error(`${provider}支付测试失败`)
    }
  }

  const generateEncryptionKey = () => {
    const key = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
    updateSetting('security', 'encryptionKey', key)
    toast.success('已生成新的加密密钥')
  }

  const generateJwtSecret = () => {
    const secret = Array.from(crypto.getRandomValues(new Uint8Array(64)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
    updateSetting('security', 'jwtSecret', secret)
    toast.success('已生成新的JWT密钥')
  }

  const getValidationError = (category: string, field: string): string | undefined => {
    return validationErrors[`${category}.${field}`]?.[0]
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* 页面标题和操作按钮 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">系统设置</h1>
            <p className="text-gray-600 mt-2">配置系统参数、安全设置和功能选项</p>
          </div>
          <div className="flex items-center space-x-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 flex items-center">
                ⚠️
                有未保存的更改
              </span>
            )}
            {lastSaveTime && (
              <span className="text-sm text-green-600 flex items-center">
                ✅
                已保存于 {lastSaveTime.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-wrap gap-3 mt-6">
          <button
            onClick={saveSettings}
            disabled={saving || !hasChanges}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <span className="mr-2">🔄</span>
            ) : (
              <span className="mr-2">✅</span>
            )}
            保存设置
          </button>

          <button
            onClick={exportSettings}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
          >
            <span className="mr-2">📥</span>
            导出设置
          </button>

          <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center">
            <span className="mr-2">📤</span>
            导入设置
            <input
              type="file"
              accept=".json"
              onChange={importSettings}
              className="hidden"
            />
          </label>

          <button
            onClick={resetToDefaults}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
          >
            <span className="mr-2">🔄</span>
            重置默认
          </button>
        </div>

        {/* 管理操作：提现手续费、推广结算与软件下载 */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 教师提现手续费设置 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <span className="mr-2">💰</span>
              教师提现手续费设置
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">手续费率 (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={withdrawalFeeConfig.teacher_withdrawal_fee_rate}
                  onChange={(e) => setWithdrawalFeeConfig(prev => ({
                    ...prev,
                    teacher_withdrawal_fee_rate: parseFloat(e.target.value) || 0
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">教师提现时收取的手续费比例</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">最低提现 ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={withdrawalFeeConfig.min_withdrawal_amount}
                    onChange={(e) => setWithdrawalFeeConfig(prev => ({
                      ...prev,
                      min_withdrawal_amount: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">最高提现 ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={withdrawalFeeConfig.max_withdrawal_amount}
                    onChange={(e) => setWithdrawalFeeConfig(prev => ({
                      ...prev,
                      max_withdrawal_amount: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    placeholder="10000"
                  />
                </div>
              </div>
              <button
                className="w-full px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                onClick={saveWithdrawalFeeConfig}
                disabled={withdrawalFeeLoading}
              >
                {withdrawalFeeLoading ? '保存中...' : '保存手续费设置'}
              </button>
              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                <p>💡 示例：手续费率 5%，提现 $100</p>
                <p>• 手续费：$5.00</p>
                <p>• 实际到账：$95.00</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">推广结算与冻结设置</h3>
            <div className="flex items-center gap-2 mb-3">
              <input
                type="number"
                value={referrerId}
                onChange={(e) => setReferrerId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded"
                placeholder="推广人ID"
              />
              <input
                type="number"
                value={freezeDays}
                onChange={(e) => setFreezeDays(e.target.value)}
                className="w-24 px-3 py-2 border border-gray-300 rounded"
                placeholder="冻结天数"
              />
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded"
                onClick={async () => {
                  if (!referrerId) { toast.error('请输入推广人ID'); return }
                  const resp = await referralAdminApi.setFreezeDays(parseInt(referrerId), parseInt(freezeDays))
                  if (resp.success) toast.success('冻结天数已更新'); else toast.error(resp.error?.message || '更新失败')
                }}
              >设置冻结天数</button>
            </div>
            <button
              className="px-3 py-2 bg-purple-600 text-white rounded"
              onClick={async () => {
                const resp = await referralAdminApi.runSettlement()
                if (resp.success) toast.success(`已结算 ${resp.data?.processed || 0} 条`); else toast.error(resp.error?.message || '结算失败')
              }}
            >运行结算任务</button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">软件下载管理</h3>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <input
                type="text"
                value={softwareMeta.name}
                onChange={(e) => setSoftwareMeta({ ...softwareMeta, name: e.target.value })}
                placeholder="软件名称"
                className="px-3 py-2 border border-gray-300 rounded"
              />
              <input
                type="text"
                value={softwareMeta.version}
                onChange={(e) => setSoftwareMeta({ ...softwareMeta, version: e.target.value })}
                placeholder="版本"
                className="px-3 py-2 border border-gray-300 rounded"
              />
              <input
                type="text"
                value={softwareMeta.platform}
                onChange={(e) => setSoftwareMeta({ ...softwareMeta, platform: e.target.value })}
                placeholder="平台（windows/mac/linux）"
                className="px-3 py-2 border border-gray-300 rounded"
              />
              <input
                type="text"
                value={softwareMeta.notes}
                onChange={(e) => setSoftwareMeta({ ...softwareMeta, notes: e.target.value })}
                placeholder="更新说明（可选）"
                className="px-3 py-2 border border-gray-300 rounded"
              />
            </div>
            <input type="file" onChange={(e) => setSoftwareFile(e.target.files?.[0] || null)} className="mb-3" />
            <div className="flex gap-2">
              <button
                className="px-3 py-2 bg-green-600 text-white rounded"
                onClick={async () => {
                  try {
                    if (!softwareFile) { toast.error('请选择文件'); return }
                    const up = await apiClient.upload<{ success: boolean; data?: { url: string } }>(
                      '/api/v1/upload', softwareFile, { type: 'software' }
                    )
                    const url = (up.data as any)?.url || (up as any)?.data?.url
                    if (!url) { toast.error('上传失败'); return }
                    const resp = await softwareAdminApi.upload({
                      software_name: softwareMeta.name,
                      version: softwareMeta.version,
                      platform: softwareMeta.platform,
                      download_url: url,
                      release_notes: softwareMeta.notes
                    })
                    if (resp.success) toast.success('软件元数据已保存'); else toast.error(resp.error?.message || '保存失败')
                  } catch (err: any) {
                    toast.error(err.message || '上传失败')
                  }
                }}
              >上传并保存</button>
              <button
                className="px-3 py-2 bg-gray-600 text-white rounded"
                onClick={async () => {
                  const list = await softwareAdminApi.list()
                  if (list.success) {
                    toast.success(`已获取 ${Array.isArray(list.data) ? list.data.length : 0} 条`) 
                  } else {
                    toast.error(list.error?.message || '获取失败')
                  }
                }}
              >获取列表</button>
            </div>
          </div>
        </div>
      </div>

      {/* 设置选项组 */}
      <div className="space-y-6">
        {/* 基础系统配置 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection('basic')}
          >
            <div className="flex items-center">
              <span className="text-blue-600 mr-3 text-2xl">🌐</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">基础系统配置</h2>
                <p className="text-sm text-gray-600">网站基本信息、维护模式、时区和语言设置</p>
              </div>
            </div>
            {expandedSections.has('basic') ? (
              <span className="text-gray-400">▲</span>
            ) : (
              <span className="text-gray-400">▼</span>
            )}
          </div>

          {expandedSections.has('basic') && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    网站名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={settings.basic.siteName}
                    onChange={(e) => updateSetting('basic', 'siteName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      getValidationError('basic', 'siteName') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="输入网站名称"
                  />
                  {getValidationError('basic', 'siteName') && (
                    <p className="text-red-500 text-sm mt-1">{getValidationError('basic', 'siteName')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    网站Logo URL
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="url"
                      value={settings.basic.siteLogo}
                      onChange={(e) => updateSetting('basic', 'siteLogo', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="https://example.com/logo.png"
                    />
                    <button
                      onClick={() => window.open(settings.basic.siteLogo, '_blank')}
                      disabled={!settings.basic.siteLogo}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      <span>📷</span>
                    </button>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    网站描述
                  </label>
                  <textarea
                    value={settings.basic.siteDescription}
                    onChange={(e) => updateSetting('basic', 'siteDescription', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="输入网站描述"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    联系邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={settings.basic.contactEmail}
                    onChange={(e) => updateSetting('basic', 'contactEmail', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      getValidationError('basic', 'contactEmail') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="support@talknect.com"
                  />
                  {getValidationError('basic', 'contactEmail') && (
                    <p className="text-red-500 text-sm mt-1">{getValidationError('basic', 'contactEmail')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    联系电话
                  </label>
                  <input
                    type="tel"
                    value={settings.basic.contactPhone}
                    onChange={(e) => updateSetting('basic', 'contactPhone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="+86-400-123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    联系地址
                  </label>
                  <input
                    type="text"
                    value={settings.basic.contactAddress}
                    onChange={(e) => updateSetting('basic', 'contactAddress', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="输入联系地址"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    时区
                  </label>
                  <select
                    value={settings.basic.timezone}
                    onChange={(e) => updateSetting('basic', 'timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {timezoneOptions.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    默认语言
                  </label>
                  <select
                    value={settings.basic.language}
                    onChange={(e) => updateSetting('basic', 'language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {languageOptions.map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.basic.maintenanceMode}
                        onChange={(e) => updateSetting('basic', 'maintenanceMode', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label className="ml-3">
                        <span className="text-sm font-medium text-gray-900">启用维护模式</span>
                        <p className="text-sm text-gray-600">启用后用户将无法访问网站前台</p>
                      </label>
                    </div>
                    {settings.basic.maintenanceMode && (
                      <span className="text-amber-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        维护中
                      </span>
                    )}
                  </div>
                </div>

                {settings.basic.maintenanceMode && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      维护公告
                    </label>
                    <textarea
                      value={settings.basic.maintenanceMessage}
                      onChange={(e) => updateSetting('basic', 'maintenanceMessage', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="输入维护期间显示的公告信息"
                    />
                  </div>
                )}

                <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-gray-400 mr-2">ℹ️</span>
                    <span className="text-sm text-gray-600">
                      当前系统版本: <span className="font-mono bg-gray-200 px-2 py-1 rounded">{settings.basic.version}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 用户设置 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection('user')}
          >
            <div className="flex items-center">
              <span className="text-green-600 mr-3 text-2xl">👤</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">用户设置</h2>
                <p className="text-sm text-gray-600">注册配置、默认头像、用户权限和账户安全策略</p>
              </div>
            </div>
            {expandedSections.has('user') ? (
              <span className="text-gray-400">▲</span>
            ) : (
              <span className="text-gray-400">▼</span>
            )}
          </div>

          {expandedSections.has('user') && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.user.allowRegistration}
                        onChange={(e) => updateSetting('user', 'allowRegistration', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label className="ml-3">
                        <span className="text-sm font-medium text-gray-900">允许用户注册</span>
                        <p className="text-sm text-gray-600">关闭后将停止新用户注册</p>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.user.requireEmailVerification}
                        onChange={(e) => updateSetting('user', 'requireEmailVerification', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label className="ml-3">
                        <span className="text-sm font-medium text-gray-900">强制邮箱验证</span>
                        <p className="text-sm text-gray-600">新注册用户必须验证邮箱后才能使用</p>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    默认头像
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="url"
                      value={settings.user.defaultAvatar}
                      onChange={(e) => updateSetting('user', 'defaultAvatar', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="默认头像URL"
                    />
                    <button
                      onClick={() => window.open(settings.user.defaultAvatar, '_blank')}
                      disabled={!settings.user.defaultAvatar}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      <span>👤</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    默认用户角色
                  </label>
                  <select
                    value={settings.user.defaultUserRole}
                    onChange={(e) => updateSetting('user', 'defaultUserRole', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="student">学生</option>
                    <option value="teacher">教师</option>
                    <option value="referrer">推荐人</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    密码最小长度 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="6"
                    max="32"
                    value={settings.user.passwordMinLength}
                    onChange={(e) => updateSetting('user', 'passwordMinLength', parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      getValidationError('user', 'passwordMinLength') ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {getValidationError('user', 'passwordMinLength') && (
                    <p className="text-red-500 text-sm mt-1">{getValidationError('user', 'passwordMinLength')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    最大登录尝试次数
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={settings.user.maxLoginAttempts}
                    onChange={(e) => updateSetting('user', 'maxLoginAttempts', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    登录锁定时间 (分钟)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1440"
                    value={settings.user.loginLockoutTime}
                    onChange={(e) => updateSetting('user', 'loginLockoutTime', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    会话超时时间 (小时)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={settings.user.sessionTimeout}
                    onChange={(e) => updateSetting('user', 'sessionTimeout', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">密码强度要求</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.user.passwordRequireUppercase}
                          onChange={(e) => updateSetting('user', 'passwordRequireUppercase', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">必须包含大写字母</span>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.user.passwordRequireNumbers}
                          onChange={(e) => updateSetting('user', 'passwordRequireNumbers', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">必须包含数字</span>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.user.passwordRequireSymbols}
                          onChange={(e) => updateSetting('user', 'passwordRequireSymbols', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">必须包含特殊字符</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 邮件配置 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection('email')}
          >
            <div className="flex items-center">
              <span className="text-orange-600 mr-3 text-2xl">✉️</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">邮件配置</h2>
                <p className="text-sm text-gray-600">SMTP服务器设置、邮件模板管理和通知配置</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  testEmailSettings()
                }}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center"
              >
                <span className="mr-1">▶️</span>
                测试邮件
              </button>
              {expandedSections.has('email') ? (
                <span className="text-gray-400">▲</span>
              ) : (
                <span className="text-gray-400">▼</span>
              )}
            </div>
          </div>

          {expandedSections.has('email') && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMTP 服务器
                  </label>
                  <input
                    type="text"
                    value={settings.email.smtpHost}
                    onChange={(e) => updateSetting('email', 'smtpHost', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="smtp.gmail.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMTP 端口
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={settings.email.smtpPort}
                    onChange={(e) => updateSetting('email', 'smtpPort', parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      getValidationError('email', 'smtpPort') ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {getValidationError('email', 'smtpPort') && (
                    <p className="text-red-500 text-sm mt-1">{getValidationError('email', 'smtpPort')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMTP 用户名
                  </label>
                  <input
                    type="text"
                    value={settings.email.smtpUsername}
                    onChange={(e) => updateSetting('email', 'smtpUsername', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="your-email@gmail.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMTP 密码
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type={showPasswords.has('smtpPassword') ? 'text' : 'password'}
                      value={settings.email.smtpPassword}
                      onChange={(e) => updateSetting('email', 'smtpPassword', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="输入SMTP密码"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('smtpPassword')}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                    >
                      {showPasswords.has('smtpPassword') ? (
                        <span>🙈</span>
                      ) : (
                        <span>👁️</span>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    加密方式
                  </label>
                  <select
                    value={settings.email.smtpEncryption}
                    onChange={(e) => updateSetting('email', 'smtpEncryption', e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="none">无加密</option>
                    <option value="ssl">SSL</option>
                    <option value="tls">TLS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    发件人邮箱
                  </label>
                  <input
                    type="email"
                    value={settings.email.fromEmail}
                    onChange={(e) => updateSetting('email', 'fromEmail', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="noreply@talknect.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    发件人名称
                  </label>
                  <input
                    type="text"
                    value={settings.email.fromName}
                    onChange={(e) => updateSetting('email', 'fromName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="TalkNect"
                  />
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">邮件通知设置</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.email.enableWelcomeEmail}
                          onChange={(e) => updateSetting('email', 'enableWelcomeEmail', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">欢迎邮件</span>
                          <p className="text-sm text-gray-600">新用户注册后发送欢迎邮件</p>
                        </div>
                      </label>
                      <span className="text-blue-600 text-xl">✉️</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.email.enablePasswordResetEmail}
                          onChange={(e) => updateSetting('email', 'enablePasswordResetEmail', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">密码重置邮件</span>
                          <p className="text-sm text-gray-600">用户申请密码重置时发送</p>
                        </div>
                      </label>
                      <span className="text-amber-600 text-xl">🔑</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.email.enableOrderNotificationEmail}
                          onChange={(e) => updateSetting('email', 'enableOrderNotificationEmail', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">订单通知邮件</span>
                          <p className="text-sm text-gray-600">课程预约和支付完成后发送</p>
                        </div>
                      </label>
                      <span className="text-green-600 text-xl">💳</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 支付配置 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection('payment')}
          >
            <div className="flex items-center">
              <span className="text-yellow-600 mr-3 text-2xl">💳</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">支付配置</h2>
                <p className="text-sm text-gray-600">支付网关设置、手续费配置和退款策略</p>
              </div>
            </div>
            {expandedSections.has('payment') ? (
              <span className="text-gray-400">▲</span>
            ) : (
              <span className="text-gray-400">▼</span>
            )}
          </div>

          {expandedSections.has('payment') && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {/* 第三方支付通道管理（推荐使用）*/}
              <div className="mt-6 mb-8">
                <div className="flex items-center mb-4">
                  <span className="text-purple-600 mr-2 text-xl">🔌</span>
                  <h3 className="text-lg font-semibold text-gray-900">第三方支付通道（推荐）</h3>
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">易于配置</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  使用第三方聚合支付（如ZPAY易支付），无需对接复杂的官方API，配置简单、上手快。支持微信、支付宝等多种支付方式。
                </p>
                <PaymentChannelsManagement />
              </div>

              {/* 分隔线 */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">或者使用官方直连支付（需要企业资质）</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 支付宝配置 */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.payment.enableAlipay}
                        onChange={(e) => updateSetting('payment', 'enableAlipay', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">启用支付宝支付</span>
                    </label>
                    <button
                      onClick={() => testPaymentSettings('支付宝')}
                      disabled={!settings.payment.enableAlipay}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                    >
                      <span className="mr-1">▶️</span>
                      测试
                    </button>
                  </div>

                  {settings.payment.enableAlipay && (
                    <div className="space-y-4 pl-7 border-l-2 border-blue-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">应用ID (App ID)</label>
                        <input
                          type="text"
                          value={settings.payment.alipayAppId}
                          onChange={(e) => updateSetting('payment', 'alipayAppId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder="输入支付宝应用ID"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">私钥</label>
                        <div className="flex space-x-2">
                          <input
                            type={showPasswords.has('alipayPrivateKey') ? 'text' : 'password'}
                            value={settings.payment.alipayPrivateKey}
                            onChange={(e) => updateSetting('payment', 'alipayPrivateKey', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="输入支付宝应用私钥"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('alipayPrivateKey')}
                            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                          >
                            {showPasswords.has('alipayPrivateKey') ? (
                              <span>🙈</span>
                            ) : (
                              <span>👁️</span>
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">支付宝公钥</label>
                        <textarea
                          value={settings.payment.alipayPublicKey}
                          onChange={(e) => updateSetting('payment', 'alipayPublicKey', e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder="输入支付宝公钥"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* 微信支付配置 */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.payment.enableWechatPay}
                        onChange={(e) => updateSetting('payment', 'enableWechatPay', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">启用微信支付</span>
                    </label>
                    <button
                      onClick={() => testPaymentSettings('微信支付')}
                      disabled={!settings.payment.enableWechatPay}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                    >
                      <span className="mr-1">▶️</span>
                      测试
                    </button>
                  </div>

                  {settings.payment.enableWechatPay && (
                    <div className="space-y-4 pl-7 border-l-2 border-green-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">应用ID</label>
                        <input
                          type="text"
                          value={settings.payment.wechatAppId}
                          onChange={(e) => updateSetting('payment', 'wechatAppId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder="输入微信应用ID"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">商户号</label>
                        <input
                          type="text"
                          value={settings.payment.wechatMchId}
                          onChange={(e) => updateSetting('payment', 'wechatMchId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder="输入微信商户号"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">API密钥</label>
                        <div className="flex space-x-2">
                          <input
                            type={showPasswords.has('wechatApiKey') ? 'text' : 'password'}
                            value={settings.payment.wechatApiKey}
                            onChange={(e) => updateSetting('payment', 'wechatApiKey', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="输入微信支付API密钥"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('wechatApiKey')}
                            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                          >
                            {showPasswords.has('wechatApiKey') ? (
                              <span>🙈</span>
                            ) : (
                              <span>👁️</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* PayPal配置 */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 bg-indigo-50 border border-indigo-200 rounded-lg mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.payment.enablePayPal}
                        onChange={(e) => updateSetting('payment', 'enablePayPal', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">启用PayPal支付</span>
                    </label>
                    <button
                      onClick={() => testPaymentSettings('PayPal')}
                      disabled={!settings.payment.enablePayPal}
                      className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                    >
                      <span className="mr-1">▶️</span>
                      测试
                    </button>
                  </div>

                  {settings.payment.enablePayPal && (
                    <div className="space-y-4 pl-7 border-l-2 border-indigo-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">客户端ID</label>
                        <input
                          type="text"
                          value={settings.payment.payPalClientId}
                          onChange={(e) => updateSetting('payment', 'payPalClientId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder="输入PayPal客户端ID"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">客户端密钥</label>
                        <div className="flex space-x-2">
                          <input
                            type={showPasswords.has('payPalClientSecret') ? 'text' : 'password'}
                            value={settings.payment.payPalClientSecret}
                            onChange={(e) => updateSetting('payment', 'payPalClientSecret', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="输入PayPal客户端密钥"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('payPalClientSecret')}
                            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                          >
                            {showPasswords.has('payPalClientSecret') ? (
                              <span>🙈</span>
                            ) : (
                              <span>👁️</span>
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">环境模式</label>
                        <select
                          value={settings.payment.payPalMode}
                          onChange={(e) => updateSetting('payment', 'payPalMode', e.target.value as any)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="sandbox">沙盒环境 (测试)</option>
                          <option value="live">生产环境 (正式)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* 通用支付设置 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    默认货币
                  </label>
                  <select
                    value={settings.payment.currency}
                    onChange={(e) => updateSetting('payment', 'currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {currencyOptions.map(currency => (
                      <option key={currency.code} value={currency.code}>{currency.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    税率 (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.payment.taxRate}
                    onChange={(e) => updateSetting('payment', 'taxRate', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    退款政策
                  </label>
                  <textarea
                    value={settings.payment.refundPolicy}
                    onChange={(e) => updateSetting('payment', 'refundPolicy', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="输入退款政策说明"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 安全和隐私 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection('security')}
          >
            <div className="flex items-center">
              <span className="text-red-600 mr-3 text-2xl">🛡️</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">安全和隐私</h2>
                <p className="text-sm text-gray-600">JWT配置、数据加密设置、隐私政策和服务条款</p>
              </div>
            </div>
            {expandedSections.has('security') ? (
              <span className="text-gray-400">▲</span>
            ) : (
              <span className="text-gray-400">▼</span>
            )}
          </div>

          {expandedSections.has('security') && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* JWT配置 */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">JWT配置</h3>
                  <div className="space-y-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        JWT密钥
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type={showPasswords.has('jwtSecret') ? 'text' : 'password'}
                          value={settings.security.jwtSecret}
                          onChange={(e) => updateSetting('security', 'jwtSecret', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                          placeholder="点击生成安全密钥"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('jwtSecret')}
                          className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                        >
                          {showPasswords.has('jwtSecret') ? (
                            <span>🙈</span>
                          ) : (
                            <span>👁️</span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={generateJwtSecret}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          <span>🔑</span>
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        建议使用至少32字符的随机密钥
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          访问令牌过期时间 (小时)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="168"
                          value={settings.security.jwtExpirationTime}
                          onChange={(e) => updateSetting('security', 'jwtExpirationTime', parseInt(e.target.value))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                            getValidationError('security', 'jwtExpirationTime') ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {getValidationError('security', 'jwtExpirationTime') && (
                          <p className="text-red-500 text-sm mt-1">{getValidationError('security', 'jwtExpirationTime')}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          刷新令牌过期时间 (小时)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="8760"
                          value={settings.security.jwtRefreshExpirationTime}
                          onChange={(e) => updateSetting('security', 'jwtRefreshExpirationTime', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 数据加密 */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">数据加密</h3>
                  <div className="space-y-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.security.enableDataEncryption}
                          onChange={(e) => updateSetting('security', 'enableDataEncryption', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">启用数据加密</span>
                      </label>
                      <span className="text-purple-600 text-xl">🔒</span>
                    </div>

                    {settings.security.enableDataEncryption && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          加密密钥
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type={showPasswords.has('encryptionKey') ? 'text' : 'password'}
                            value={settings.security.encryptionKey}
                            onChange={(e) => updateSetting('security', 'encryptionKey', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                            placeholder="点击生成加密密钥"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('encryptionKey')}
                            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                          >
                            {showPasswords.has('encryptionKey') ? (
                              <span>🙈</span>
                            ) : (
                              <span>👁️</span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={generateEncryptionKey}
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            <span>🔑</span>
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          用于敏感数据加密的AES-256密钥
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* GDPR合规 */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">隐私保护</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.security.gdprCompliance}
                          onChange={(e) => updateSetting('security', 'gdprCompliance', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">GDPR合规</span>
                          <p className="text-sm text-gray-600">符合欧盟通用数据保护条例</p>
                        </div>
                      </label>
                      <span className="text-green-600 text-xl">🛡️</span>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        隐私政策
                      </label>
                      <textarea
                        value={settings.security.privacyPolicy}
                        onChange={(e) => updateSetting('security', 'privacyPolicy', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="输入隐私政策内容"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        服务条款
                      </label>
                      <textarea
                        value={settings.security.termsOfService}
                        onChange={(e) => updateSetting('security', 'termsOfService', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="输入服务条款内容"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cookie政策
                      </label>
                      <textarea
                        value={settings.security.cookiePolicy}
                        onChange={(e) => updateSetting('security', 'cookiePolicy', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="输入Cookie政策说明"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 备份和维护 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div
            className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection('backup')}
          >
            <div className="flex items-center">
              <span className="text-indigo-600 mr-3 text-2xl">🖥️</span>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">备份和维护</h2>
                <p className="text-sm text-gray-600">数据库备份配置、系统清理设置和性能监控</p>
              </div>
            </div>
            {expandedSections.has('backup') ? (
              <span className="text-gray-400">▲</span>
            ) : (
              <span className="text-gray-400">▼</span>
            )}
          </div>

          {expandedSections.has('backup') && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* 数据库备份 */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">数据库备份</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.backup.enableAutoBackup}
                          onChange={(e) => updateSetting('backup', 'enableAutoBackup', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">自动备份</span>
                          <p className="text-sm text-gray-600">定期自动备份数据库</p>
                        </div>
                      </label>
                      <span className="text-blue-600 text-xl">📦</span>
                    </div>

                    {settings.backup.enableAutoBackup && (
                      <div className="grid grid-cols-2 gap-4 pl-7">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            备份频率
                          </label>
                          <select
                            value={settings.backup.backupFrequency}
                            onChange={(e) => updateSetting('backup', 'backupFrequency', e.target.value as any)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          >
                            <option value="daily">每日</option>
                            <option value="weekly">每周</option>
                            <option value="monthly">每月</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            保留天数
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={settings.backup.backupRetention}
                            onChange={(e) => updateSetting('backup', 'backupRetention', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        备份存储路径
                      </label>
                      <input
                        type="text"
                        value={settings.backup.backupLocation}
                        onChange={(e) => updateSetting('backup', 'backupLocation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="/backups"
                      />
                    </div>
                  </div>
                </div>

                {/* 系统清理 */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">系统清理</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.backup.enableLogCleanup}
                          onChange={(e) => updateSetting('backup', 'enableLogCleanup', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">日志清理</span>
                          <p className="text-sm text-gray-600">定期清理过期日志文件</p>
                        </div>
                      </label>
                      <span className="text-amber-600 text-xl">🔧</span>
                    </div>

                    {settings.backup.enableLogCleanup && (
                      <div className="pl-7">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          日志保留天数
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={settings.backup.logRetentionDays}
                          onChange={(e) => updateSetting('backup', 'logRetentionDays', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.backup.enableTempCleanup}
                          onChange={(e) => updateSetting('backup', 'enableTempCleanup', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">临时文件清理</span>
                          <p className="text-sm text-gray-600">定期清理临时文件和缓存</p>
                        </div>
                      </label>
                      <span className="text-green-600 text-xl">🚚</span>
                    </div>

                    {settings.backup.enableTempCleanup && (
                      <div className="pl-7">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          清理频率
                        </label>
                        <select
                          value={settings.backup.tempCleanupFrequency}
                          onChange={(e) => updateSetting('backup', 'tempCleanupFrequency', e.target.value as any)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="daily">每日</option>
                          <option value="weekly">每周</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* 监控和报告 */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">监控和报告</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.backup.performanceMonitoring}
                          onChange={(e) => updateSetting('backup', 'performanceMonitoring', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">性能监控</span>
                          <p className="text-sm text-gray-600">监控系统性能指标和资源使用</p>
                        </div>
                      </label>
                      <span className="text-purple-600 text-xl">📊</span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.backup.errorReporting}
                          onChange={(e) => updateSetting('backup', 'errorReporting', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">错误报告</span>
                          <p className="text-sm text-gray-600">自动收集和报告系统错误</p>
                        </div>
                      </label>
                      <span className="text-red-600 text-xl">⚠️</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 底部操作栏 */}
      <div className="mt-8 flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600">
          <span className="inline mr-1">ℹ️</span>
          更改系统配置可能会影响平台正常运行，请谨慎操作
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            取消
          </button>
          <button
            onClick={saveSettings}
            disabled={saving || !hasChanges}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <span className="mr-2">🔄</span>
            ) : (
              <span className="mr-2">✅</span>
            )}
            保存所有设置
          </button>
        </div>
      </div>
    </div>
  )
}
